import { ref } from "vue";
import Mango from "./mango";
import { purgePoisonedIdentity, readStoredMemberId, readStoredToken, restoreShopper } from "./browserSession.js";
import { getSessionApi } from "./sessionApi.js";

const getUser = async (user) => {
    user = user || { value: {} };

    const injected = window.user?.id ? window.user : null;
    const sessionApi = getSessionApi();

    // No session API to ask. Keep the injected shopper, including the stale-data clear.
    if (injected && !sessionApi) {
        console.log("got it!");
        user.value = injected;
        window.user = null;
        return user.value;
    }

    let restored = null;
    try {
        restored = await restoreShopper();
        if (restored?.id) {
            user.value = restored;
            window.user = null;
            return user.value;
        }
    } catch {
        restored = { unavailable: true };
    }

    // Network failure or HTTP 5xx, including 503 session_store_unavailable.
    // Keep the injected shopper. Do not clear storage.
    if (restored?.unavailable && injected) {
        user.value = injected;
        return user.value;
    }

    // An identity stored as the literal "undefined" by an older build is a
    // string, so it survives a truthiness check and sends the shopper to
    // GET /members/undefined, which the API rejects with a 401. Drop it here so
    // the browser heals instead of repeating that request on every page load.
    purgePoisonedIdentity();
    const userId = readStoredMemberId();
    const token = readStoredToken();

    // The cookie session could not be checked. Do not drop the shopper or load
    // /members/:id with an empty Authorization header.
    if (restored?.unavailable && userId && !token) {
        const error = new Error("session unavailable");
        error.code = "ECONNABORTED";
        throw error;
    }

    // The API is up and rejected the session. A leftover user id is not a session.
    if (sessionApi && !token && !restored?.id && !restored?.unavailable) {
        try {
            window.localStorage.removeItem("user");
            window.localStorage.removeItem("email");
        } catch {
            // private mode
        }
        window.user = null;
        user.value = {};
        return user.value;
    }

    if (!userId || !token) return {};

    user.value = await Mango.member(userId, { depthLimit: 0 }).then(
        (r) => (user.value = r)
    );

    return user.value;
};

const initUser = async () => {
    const user = ref({});
    await getUser(user);
    return user;
};

const accountCreateErrorMessage = (payload) => {
	const data = payload?.response?.data ?? payload ?? {}
	const raw = data.error || data.message || data.errors
	const text =
		typeof raw === 'object' && raw !== null
			? Object.values(raw).filter(Boolean).join('. ')
			: raw

	if (typeof text === 'string' && /already in use/i.test(text)) {
		return 'An account with this email already exists. Please sign in or use a different email address.'
	}
	if (typeof text === 'string' && text.trim() && !/^error creating account$/i.test(text.trim())) {
		return text.replace(/^Error creating customers:\s*/i, '')
	}
	return 'We could not create your account. Please try again or contact support if the problem continues.'
}

export { initUser, getUser, accountCreateErrorMessage };
